import { useEffect } from "react";
import { Carrot } from "@/components/Carrot";
import { Reveal } from "@/components/Reveal";
import { SiteNav } from "@/components/SiteNav";
import { CallToAction } from "@/components/sections/CallToAction";
import { Footer } from "@/components/sections/Footer";

// Short, declarative tenets — not a process, so deliberately unnumbered. Each
// is a belief plus the practical consequence of holding it.
const BELIEFS = [
  {
    claim: "Import, not input.",
    body: "The work already produces records: vet PDFs, farrier spreadsheets, registration papers, group texts, voice notes. Our job is to read what's there, not to make you retype it into yet another form.",
  },
  {
    claim: "The barn is the source of truth.",
    body: "We build for how barns run at 6am in the mud, with cold hands and no time, not for how a tidy database wishes they would. If it doesn't survive the aisle, it doesn't ship.",
  },
  {
    claim: "Structure is a side effect.",
    body: "You run the ranch; the record assembles itself behind you. Nobody should have to stop working to keep the software happy.",
  },
  {
    claim: "Trust needs receipts.",
    body: "Every fact traces back to where it came from. A horse's history should stand up to a buyer, a vet, or an insurer, because you can show exactly where each line was learned.",
  },
];

export function About() {
  useEffect(() => {
    document.title = "About | CarrotOnTheHorse";
  }, []);

  return (
    <div className="min-h-screen">
      <SiteNav current="/about" />
      <main>
        {/* Intro */}
        <section className="relative overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-40 left-1/2 h-[40rem] w-[40rem] -translate-x-1/2 rounded-full opacity-50 blur-3xl"
            style={{ background: "radial-gradient(closest-side, var(--carrot-soft), transparent)" }}
          />
          <div className="relative mx-auto max-w-6xl px-5 pt-16 sm:px-8 sm:pt-24">
            <Reveal className="max-w-3xl">
              <h1 className="text-[clamp(2.1rem,7vw,2.6rem)] font-extrabold leading-[1.02] text-ink sm:text-balance sm:text-6xl sm:leading-[1.0]">
                The <span className="text-field">operating memory</span> for the horse world.
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink/70 sm:text-xl">
                Horse operations run on skill, instinct, and a thousand scattered artifacts, and almost none
                of it lives anywhere you can search. Carrot On The Horse is the layer that remembers, captured
                from what you already create and turned into facts you can act on.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Origin */}
        <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8 sm:py-28">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
            <Reveal>
              <h2 className="text-4xl font-extrabold leading-[1.05] text-ink sm:text-5xl">
                It started in the <span className="text-field">barn aisle</span>.
              </h2>
            </Reveal>
            <div className="space-y-6 text-lg leading-relaxed text-ink/70 lg:pt-2">
              <Reveal>
                <p>
                  One of us spent five years inside this world, as a vet assistant, groomer, and handler on a
                  working ranch, watching critical knowledge live in someone's head, a buried group text, or a
                  folder of vet PDFs nobody could find again. When a horse changed hands or a key person left,
                  the history walked out the door with them.
                </p>
              </Reveal>
              <Reveal delay={90}>
                <p>
                  The other spent fifteen years building software, the last several teaching AI to read messy,
                  high-stakes documents and turn them into records you can trust. Carrot On The Horse is what
                  happens when those two sit at the same table: a product that meets the barn where it works
                  and does the structuring quietly, behind the scenes.
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Beliefs — the signature */}
        <section className="relative overflow-hidden bg-field-deep text-cream">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, var(--cream) 1px, transparent 0)",
              backgroundSize: "26px 26px",
            }}
          />
          <div className="relative mx-auto max-w-6xl px-5 py-24 sm:px-8 sm:py-28">
            <Reveal className="max-w-2xl">
              <h2 className="text-4xl font-extrabold leading-[1.05] sm:text-5xl">
                Four convictions we won't <span className="text-carrot">trade away</span>.
              </h2>
            </Reveal>

            <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-cream/15 bg-cream/15 sm:grid-cols-2">
              {BELIEFS.map((b, i) => (
                <Reveal key={b.claim} delay={(i % 2) * 90}>
                  <div className="h-full bg-field-deep p-7 transition-colors duration-300 hover:bg-cream/[0.06] sm:p-9">
                    <h3 className="font-display text-2xl font-bold leading-tight text-cream">{b.claim}</h3>
                    <p className="mt-4 leading-relaxed text-cream/70">{b.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Where it goes */}
        <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8 sm:py-28">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <Reveal>
              <h2 className="text-4xl font-extrabold leading-[1.05] text-ink sm:text-5xl">
                Start with memory. <span className="text-field">Earn the rest.</span>
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-ink/70">
                We begin where the pain is loudest: capturing what you already create and turning it into
                operational memory you can ask questions of. From there, the same structured memory becomes
                the control layer for reminders, verified history, marketplace trust, and eventually the
                cameras, sensors, and robots arriving on the modern ranch.
              </p>
              <p className="mt-6 max-w-xl text-xl leading-relaxed text-ink/60">
                The carrot was always the point. We just think you should actually catch it.
              </p>
            </Reveal>

            <Reveal delay={120} className="hidden lg:block">
              <div className="relative flex items-center justify-center">
                <div
                  aria-hidden
                  className="absolute h-64 w-64 rounded-full opacity-60 blur-3xl"
                  style={{ background: "radial-gradient(closest-side, var(--carrot-soft), transparent)" }}
                />
                <Carrot className="relative h-52 w-52 rotate-[10deg] drop-shadow-[0_24px_24px_rgba(120,60,10,0.18)]" />
              </div>
            </Reveal>
          </div>
        </section>

        <CallToAction />
      </main>
      <Footer />
    </div>
  );
}

export default About;
